import { BRAND, type MatchConfig } from '@nt2/engine';
import { narrator } from '../audio/speech.js';
import { prefs, setPrefs } from '../state/prefs.js';
import { el, on } from './dom.js';
import { icon } from './icons.js';
import { openModal } from './modal.js';
import { rulesBody } from './dialogs.js';

/**
 * A demonstration of a game being played.
 *
 * Reading the rules and watching them happen are different things, and for a
 * game whose one awkward rule is *two words that touch read as one word*, the
 * second is far more convincing: it takes one shot of CAR beside MUCH with
 * "carmuch" written underneath.
 *
 * It is a miniature of the real thing rather than the real thing driven by a
 * script — its own small board and rack, using the game's own tile styles — so
 * it can run from the front door where no match exists, it cannot interfere
 * with a match that does, and it always shows the same thing. A cursor moves,
 * buttons visibly depress, letters fly from the rack, and a caption says what
 * is happening.
 *
 * Everything is on a single timeline that can be paused, stepped or skipped:
 * an animation nobody can stop is a thing done *to* a player.
 */

const CELL = 34;
/**
 * How much longer each step stays up than the times written below. At 1 the
 * demonstration was far too quick to follow — letters landed and captions
 * changed before anybody had read them.
 */
const PACE = 3.5;
const COLS = 9;
const ROWS = 6;

interface Placed { letter: string; value: number; x: number; y: number; id: string }

/** One beat of the demonstration. */
interface Beat {
  caption: string;
  /** What to say out loud, when it should differ from the caption. */
  spoken?: string;
  /** Milliseconds this beat lasts. */
  ms: number;
  run(s: Stage): void;
}

interface Stage {
  board: HTMLElement;
  rack: HTMLElement;
  caption: HTMLElement;
  callBtn: HTMLElement;
  shuffleBtn: HTMLElement;
  stuckBtn: HTMLElement;
  score: HTMLElement;
  cursor: HTMLElement;
  note: HTMLElement;
  place(p: Placed): void;
  takeFromRack(letter: string): void;
  press(node: HTMLElement): void;
  pointAt(node: HTMLElement): void;
  setRack(letters: string[]): void;
  setScore(text: string): void;
  flash(text: string, kind?: 'good' | 'bad'): void;
  clearBoard(): void;
  /** Ring a placed letter the way holding it does on the real board. */
  select(ids: string[]): void;
  /** Slide the selected letters by whole cells, as a dragged group does. */
  nudge(dx: number, dy: number): void;
  /** Draw a freehand loop round some letters, then select them. */
  lasso(ids: string[]): void;
}

const VALUES: Record<string, number> = {
  A: 1, B: 3, C: 3, D: 2, E: 1, F: 4, G: 2, H: 4, I: 1, J: 8, K: 5, L: 1,
  M: 3, N: 1, O: 1, P: 3, Q: 10, R: 1, S: 1, T: 1, U: 1, V: 4, W: 4, X: 8,
  Y: 4, Z: 10,
};

const tile = (letter: string): HTMLElement =>
  el('div', { class: 'tile demo__tile' },
    el('span', { text: letter }),
    el('span', { class: 'tile__value', text: String(VALUES[letter] ?? 1) }));

/** The how-to-play page: a demonstration, then the rules. */
export function openHowToPlay(config?: MatchConfig): void { openDemo(config); }

export function openDemo(config?: MatchConfig): void {
  const draw = config?.drawPerRound ?? 2;

  // ---- the miniature screen ------------------------------------------
  const board = el('div', { class: 'demo__board' });
  const paper = el('div', { class: 'demo__paper' });
  board.append(paper);
  const rack = el('div', { class: 'demo__rack' });
  const cursor = el('div', { class: 'demo__cursor', 'aria-hidden': 'true' }, icon('point', 20));
  const note = el('div', { class: 'demo__note' });
  const svgNs = 'http://www.w3.org/2000/svg';
  const loop = document.createElementNS(svgNs, 'svg');
  loop.setAttribute('class', 'demo__lasso');
  const loopPath = document.createElementNS(svgNs, 'path');
  loop.append(loopPath);
  board.append(loop);

  const shuffleBtn = el('button', {
    class: 'btn btn--icon demo__btn', type: 'button', tabIndex: -1, 'aria-hidden': 'true',
  }, icon('shuffle', 15));
  const stuckBtn = el('button', {
    class: 'btn btn--icon demo__btn', type: 'button', tabIndex: -1, 'aria-hidden': 'true',
  }, icon('stuck', 15));
  const callBtn = el('button', {
    class: 'callbtn demo__call', type: 'button', tabIndex: -1, 'aria-hidden': 'true',
  }, el('span', { text: BRAND.callPhrase(draw) }));
  const score = el('div', { class: 'demo__score' }, el('b', { text: '0' }), el('span', { text: 'score' }));

  const stageEl = el('div', { class: 'demo' },
    el('div', { class: 'demo__bar' },
      el('span', { class: 'demo__brand', text: BRAND.name }), score),
    el('div', { class: 'demo__screen' }, board, note),
    el('div', { class: 'demo__tray' }, rack,
      el('div', { class: 'demo__acts' }, shuffleBtn, stuckBtn, callBtn)), cursor);

  const caption = el('p', { class: 'demo__caption' });

  // ---- the primitives the script is written in -----------------------
  const nodes = new Map<string, HTMLElement>();
  const picked = new Set<string>();
  let rackLetters: string[] = [];

  const stage: Stage = {
    board, rack, caption, callBtn, shuffleBtn, stuckBtn, score, cursor, note,

    setRack(letters) {
      rackLetters = [...letters];
      rack.replaceChildren(...rackLetters.map((L) => {
        const t = tile(L);
        t.dataset.letter = L;
        return t;
      }));
    },

    takeFromRack(letter) {
      const node = [...rack.children].find(
        (c) => (c as HTMLElement).dataset.letter === letter) as HTMLElement | undefined;
      if (!node) return;
      node.classList.add('demo__tile--gone');
      setTimeout(() => node.remove(), 220);
      rackLetters = rackLetters.filter((L, i) => !(L === letter && rackLetters.indexOf(letter) === i));
    },

    place(p) {
      const t = tile(p.letter);
      t.classList.add('demo__tile--land');
      t.style.left = `${p.x * CELL}px`;
      t.style.top = `${p.y * CELL}px`;
      nodes.set(p.id, t);
      board.append(t);
    },

    press(node) {
      node.classList.add('demo__press');
      setTimeout(() => node.classList.remove('demo__press'), 260);
    },

    /**
     * Move the pointer to an element.
     *
     * Measured against the stage, not the page: the whole demonstration sits
     * inside a dialog that can be scrolled, and a pointer positioned in page
     * coordinates would drift away from what it is pointing at.
     */
    pointAt(node) {
      const a = node.getBoundingClientRect();
      const b = stageEl.getBoundingClientRect();
      cursor.style.transform =
        `translate3d(${a.left - b.left + a.width * 0.5}px, ${a.top - b.top + a.height * 0.55}px, 0)`;
      cursor.classList.add('demo__cursor--on');
    },

    setScore(text) {
      (score.firstElementChild as HTMLElement).textContent = text;
    },

    flash(text, kind = 'good') {
      note.textContent = text;
      note.className = `demo__note demo__note--${kind} demo__note--on`;
      setTimeout(() => note.classList.remove('demo__note--on'), 1500);
    },

    clearBoard() {
      for (const n of nodes.values()) n.remove();
      nodes.clear();
      picked.clear();
      loop.classList.remove('demo__lasso--on');
    },

    select(ids) {
      picked.clear();
      for (const n of nodes.values()) n.classList.remove('tile--selected');
      for (const id of ids) {
        const n = nodes.get(id);
        if (!n) continue;
        n.classList.add('tile--selected');
        picked.add(id);
      }
    },

    /**
     * A hand-drawn loop: the letters' outline, padded, walked round with a
     * little wobble so it looks drawn rather than computed, and revealed as
     * a stroke being traced.
     */
    lasso(ids) {
      const cells = ids.map((id) => nodes.get(id)).filter((n): n is HTMLElement => !!n)
        .map((n) => ({ x: parseFloat(n.style.left) + 16, y: parseFloat(n.style.top) + 16 }));
      if (!cells.length) return;
      const cx = cells.reduce((a, c) => a + c.x, 0) / cells.length;
      const cy = cells.reduce((a, c) => a + c.y, 0) / cells.length;
      const pts: string[] = [];
      for (let i = 0; i <= 36; i++) {
        const a = (i / 36) * Math.PI * 2;
        // How far the outline must reach in this direction to clear every
        // letter, plus a margin and a wobble.
        let reach = 0;
        for (const c of cells) {
          const along = (c.x - cx) * Math.cos(a) + (c.y - cy) * Math.sin(a);
          reach = Math.max(reach, along);
        }
        const r = reach + 26 + Math.sin(i * 2.3) * 4;
        pts.push(`${i ? 'L' : 'M'}${(cx + Math.cos(a) * r).toFixed(1)} ${(cy + Math.sin(a) * r).toFixed(1)}`);
      }
      loopPath.setAttribute('d', `${pts.join(' ')} Z`);
      loop.classList.remove('demo__lasso--on');
      void loop.getBoundingClientRect();           // restart the tracing
      loop.classList.add('demo__lasso--on');
      stage.select(ids);
    },

    nudge(dx, dy) {
      for (const id of picked) {
        const n = nodes.get(id);
        if (!n) continue;
        n.style.left = `${parseFloat(n.style.left || '0') + dx * CELL}px`;
        n.style.top = `${parseFloat(n.style.top || '0') + dy * CELL}px`;
      }
    },
  };

  // ---- the script ----------------------------------------------------
  /*
   * Deliberately a real hand: CAT and CAR crossing on the C, then a bad
   * neighbour, then the fix. The letters are chosen so every point lands with
   * one short word rather than a clever one.
   */
  const beats: Beat[] = [
    { ms: 1100, caption: 'Here is a demonstration of how to play.',
      run(s) { s.setRack([]); s.setScore('0'); } },

    { ms: 2100, caption: 'Everybody starts at once with seven letters. These are yours — nobody else can see them.',
      run(s) { s.setRack(['C', 'A', 'T', 'R', 'M', 'U', 'H']); s.setScore('0'); } },

    { ms: 1500, caption: 'Drag a letter up onto the board. Or tap a square, then tap letters.',
      run(s) { s.pointAt(s.rack.children[0] as HTMLElement); } },

    { ms: 900, caption: 'C…',
      run(s) { s.takeFromRack('C'); s.place({ letter: 'C', value: 3, x: 2, y: 2, id: 'c' }); } },
    { ms: 700, caption: 'A…',
      run(s) { s.takeFromRack('A'); s.place({ letter: 'A', value: 1, x: 3, y: 2, id: 'a' }); } },
    { ms: 1600, caption: 'CAT. Words read left to right, and top to bottom. Never backwards.',
      run(s) {
        s.takeFromRack('T');
        s.place({ letter: 'T', value: 1, x: 4, y: 2, id: 't' });
        s.flash('CAT ✓');
      } },

    { ms: 2200, caption: 'Cross an existing word to start another. The C is now in two words at once — and it is paid for both.',
      run(s) {
        s.takeFromRack('A');
        s.takeFromRack('R');
        s.place({ letter: 'A', value: 1, x: 2, y: 3, id: 'a2' });
        s.place({ letter: 'R', value: 1, x: 2, y: 4, id: 'r' });
        s.flash('CAR ✓ — the C counts twice');
        s.setScore('9');
      } },

    { ms: 2600, caption: 'Now the one rule that catches everybody. Put MUCH straight after CAT, with no gap…',
      run(s) {
        for (const [i, L] of ['M', 'U', 'C', 'H'].entries()) {
          s.takeFromRack(L);
          s.place({ letter: L, value: VALUES[L] ?? 1, x: 5 + i, y: 2, id: `m${i}` });
        }
      } },

    { ms: 2800, caption: '…and the row now reads CATMUCH, which is not a word. Letters that touch in a line are one word.',
      run(s) { s.flash('CATMUCH ✗', 'bad'); s.setScore('−7'); } },

    { ms: 2400, caption: 'A gap fixes the spelling, but leaves two separate groups. That is still invalid: all your letters must form one connected block.',
      run(s) {
        s.clearBoard();
        s.place({ letter: 'C', value: 3, x: 1, y: 2, id: 'c' });
        s.place({ letter: 'A', value: 1, x: 2, y: 2, id: 'a' });
        s.place({ letter: 'T', value: 1, x: 3, y: 2, id: 't' });
        s.place({ letter: 'A', value: 1, x: 1, y: 3, id: 'a2' });
        s.place({ letter: 'R', value: 1, x: 1, y: 4, id: 'r' });
        s.place({ letter: 'M', value: 3, x: 5, y: 2, id: 'm0' });
        s.place({ letter: 'U', value: 1, x: 6, y: 2, id: 'm1' });
        s.place({ letter: 'C', value: 3, x: 7, y: 2, id: 'm2' });
        s.place({ letter: 'H', value: 4, x: 8, y: 2, id: 'm3' });
        s.flash('Two groups — not connected ✗', 'bad');
        s.setScore('20');
      } },

    /*
     * Holding letters, one at a time.
     *
     * These are the gestures nobody discovers by accident and the ones that
     * make a crowded grid workable, so they get four beats: holding letters
     * one at a time, dragging them, drawing round a group, and moving that.
     */
    { ms: 2800, caption: 'Hold a letter down and it lights up. Hold another, and another — each one on its own, leaving the rest alone.',
      run(s) {
        s.pointAt(nodes.get('m0') ?? s.board);
        s.select(['m0', 'm1', 'm2', 'm3']);
        s.flash('4 letters held');
      } },

    { ms: 3000, caption: 'Now drag any one of them and the whole set moves together, keeping its shape.',
      run(s) { s.nudge(0, 1); s.flash('moved together'); } },

    { ms: 3600, caption: 'Or hold still on empty board for a second, then draw round the letters you want — any shape at all. Let go, and everything inside the loop is picked up.',
      run(s) {
        s.select([]);
        s.pointAt(s.board);
        s.lasso(['c', 'a', 't', 'a2', 'r']);
        s.flash('5 letters inside the loop');
      } },

    { ms: 2600, caption: 'Drag any one of them and the whole shape moves. A tap on bare board puts them all down again.',
      run(s) {
        s.nudge(1, 0);
        loop.classList.remove('demo__lasso--on');
        s.flash('moved together');
      } },

    { ms: 3200, caption: 'Rearrange the same letters into MATCH, CAR and CUR. They cross to make one connected block. Every line is a word, so now the grid is ready.',
      run(s) {
        s.clearBoard();
        for (const [i, L] of ['M', 'A', 'T', 'C', 'H'].entries()) {
          s.place({ letter: L, value: VALUES[L] ?? 1, x: 1 + i, y: 1, id: `match${i}` });
        }
        s.place({ letter: 'A', value: 1, x: 4, y: 2, id: 'cross-a' });
        for (const [i, L] of ['C', 'U', 'R'].entries()) {
          s.place({ letter: L, value: VALUES[L] ?? 1, x: 2 + i, y: 3, id: `cur${i}` });
        }
        s.select([]);
        s.flash('One connected block ✓');
        s.setScore('22');
      } },

    { ms: 2000, caption: 'Stuck with letters that will not go anywhere? Press this. If everybody agrees, the whole table draws.',
      run(s) { s.pointAt(s.stuckBtn); s.press(s.stuckBtn); } },

    { ms: 1900, caption: 'And this one just reorders your rack — useful when the same seven letters stop suggesting anything.',
      run(s) { s.pointAt(s.shuffleBtn); s.press(s.shuffleBtn); } },

    { ms: 2600, caption: `Rack empty and every word real? Call ${BRAND.callPhrase(draw)} — and everyone, you included, draws ${draw} more.`,
      run(s) {
        s.setRack([]);
        s.callBtn.classList.add('callbtn--ready');
        s.pointAt(s.callBtn);
        s.press(s.callBtn);
      } },

    { ms: 2300, caption: `Everybody draws ${draw}, nobody waits, and it carries straight on. Finishing fast is how you leave the others holding letters.`,
      run(s) {
        s.callBtn.classList.remove('callbtn--ready');
        s.setRack(['E', 'O']);
        s.flash(`Everyone draws ${draw}`);
      } },

    { ms: 3000, caption: 'When the bag can no longer go round, the first player to use every letter ends the match and takes a bonus. Highest score wins.',
      run(s) { s.setRack([]); s.setScore(String(22 + (config?.goingOutBonus ?? 10))); s.flash(`+${config?.goingOutBonus ?? 10} for going out`); } },

    { ms: 2600, caption: 'Letters in words count once for each word they are in. Letters you never used are taken off. That is the whole game.',
      run(s) { s.pointAt(s.score); } },
  ];

  // ---- the player ----------------------------------------------------
  let at = -1;
  let timer = 0;
  let playing = true;

  const playBtn = el('button', { class: 'btn btn--sm', type: 'button' });
  const setPlayLabel = (): void => {
    playBtn.replaceChildren(playing ? icon('stuck', 15) : icon('play', 15));
    playBtn.append(el('span', { text: playing ? 'Pause' : 'Play' }));
    playBtn.title = playing ? 'Pause the demonstration' : 'Carry on';
  };

  /*
   * Moving on.
   *
   * A beat is over when two things have both happened: it has been on screen
   * for its own time — stretched by PACE, because at the original speed a
   * player could not take in what had just happened — and the voice, if it is
   * on, has finished its sentence. The first version moved on regardless and
   * talked over itself.
   */
  let dwelt = false;
  let spoken = true;
  const advanceIfReady = (): void => {
    if (!playing || !dwelt || !spoken) return;
    if (at + 1 >= beats.length) { playing = false; setPlayLabel(); return; }
    // Reset before running the next beat, not after: with the voice off,
    // "finished speaking" is reported at once, from inside runBeat — and with
    // the old beat's time still counted as served, every remaining beat fired
    // in the same instant and the demonstration jumped to its end.
    dwelt = false;
    runBeat(at + 1);
    schedule();
  };

  const runBeat = (i: number): void => {
    at = i;
    const beat = beats[i];
    if (!beat) return;
    // A flash from the previous beat lingering over this one's caption reads
    // as a contradiction, so each beat starts with the badge cleared.
    note.classList.remove('demo__note--on');
    beat.run(stage);
    caption.textContent = beat.caption;
    // Spoken, not read: watching the board and reading a caption under it at
    // the same time means doing neither. `spoken` drops the punctuation marks
    // a synthesiser reads out as pauses in the wrong places.
    spoken = !narrator.enabled;
    narrator.say(beat.spoken ?? beat.caption, () => { spoken = true; advanceIfReady(); });
    progress.style.width = `${((i + 1) / beats.length) * 100}%`;
  };

  const schedule = (): void => {
    window.clearTimeout(timer);
    if (!playing) return;
    dwelt = false;
    const beat = beats[at];
    timer = window.setTimeout(() => { dwelt = true; advanceIfReady(); },
      (beat?.ms ?? 1600) * PACE);
  };

  const progress = el('div', { class: 'demo__progress' });

  /**
   * Stepping back replays from the beginning.
   *
   * The beats are not individually reversible — one of them wipes the board
   * and rebuilds it — and replaying a few seconds is both simpler and less
   * confusing than a half-restored state that never existed during a real
   * game.
   */
  const jumpTo = (i: number): void => {
    stage.clearBoard();
    stage.setRack([]);
    stage.setScore('0');
    stage.callBtn.classList.remove('callbtn--ready');
    note.classList.remove('demo__note--on');
    for (let k = 0; k <= i; k++) beats[k]?.run(stage);
    // Replaying runs every beat in one frame, so the letters each beat spent
    // are all mid-fade at once. On a jump they simply go.
    for (const ghost of rack.querySelectorAll('.demo__tile--gone')) ghost.remove();
    caption.textContent = beats[i]?.caption ?? '';
    at = i;
    progress.style.width = `${((i + 1) / beats.length) * 100}%`;
  };

  /**
   * Read it out loud.
   *
   * Off until asked for, because a voice starting unbidden is startling, and
   * because iOS only honours speech that was first requested inside a real
   * gesture — which a tap on this button is. Hidden entirely where the browser
   * has no synthesiser rather than offering a button that does nothing.
   */
  const voiceBtn = el('button', { class: 'btn btn--sm', type: 'button' });
  const setVoiceLabel = (): void => {
    voiceBtn.textContent = narrator.enabled ? 'Voice on' : 'Read it to me';
    voiceBtn.classList.toggle('btn--primary', narrator.enabled);
    voiceBtn.title = narrator.enabled
      ? 'Stop reading the commentary out loud'
      : 'Read the commentary out loud while it plays';
  };
  voiceBtn.hidden = !narrator.available;
  on(voiceBtn, 'click', () => {
    const on_ = !narrator.enabled;
    narrator.enable(on_);
    setPrefs({ narrate: on_ });
    setVoiceLabel();
    if (on_) {
      spoken = false;
      narrator.say(beats[at]?.spoken ?? beats[at]?.caption ?? '',
        () => { spoken = true; advanceIfReady(); });
    } else {
      spoken = true;
      advanceIfReady();
    }
  });
  setVoiceLabel();

  const prevBtn = el('button', {
    class: 'btn btn--sm btn--ghost', type: 'button', text: 'Back', title: 'Previous step',
  });
  const nextBtn = el('button', {
    class: 'btn btn--sm btn--ghost', type: 'button', text: 'Next', title: 'Next step',
  });
  on(playBtn, 'click', () => {
    playing = !playing;
    setPlayLabel();
    if (playing) {
      if (at + 1 >= beats.length) jumpTo(0);
      // Pausing stopped the voice mid-sentence, so its "finished" will never
      // come; the next beat speaks for itself.
      spoken = true;
      schedule();
    } else {
      window.clearTimeout(timer);
      narrator.stop();
    }
  });
  on(prevBtn, 'click', () => { playing = false; setPlayLabel(); window.clearTimeout(timer); jumpTo(Math.max(0, at - 1)); });
  on(nextBtn, 'click', () => { playing = false; setPlayLabel(); window.clearTimeout(timer); jumpTo(Math.min(beats.length - 1, at + 1)); });

  setPlayLabel();

  /*
   * One page for "how do I play": the demonstration at the top, the written
   * rules of this match underneath. They used to be two separate dialogs, and
   * which one somebody opened decided whether they ever saw the other.
   */
  const body = el('div', {},
    el('p', { class: 'demo__intro', text: 'Here is a demonstration of how to play. ' +
      'The full rules are underneath.' }),
    stageEl,
    el('div', { class: 'demo__progressbar' }, progress),
    caption,
    el('div', { class: 'demo__controls' }, playBtn, prevBtn, nextBtn, voiceBtn),
    el('h3', { class: 'demo__ruleshead', text: 'The rules' }),
    rulesBody(config));

  const resize = new ResizeObserver(() => {
    stageEl.style.setProperty('--demo-scale', String(Math.min(1, stageEl.clientWidth / 340)));
  });

  openModal({
    title: `How to play ${BRAND.name}`,
    body,
    wide: true,
    onClose: () => { resize.disconnect(); window.clearTimeout(timer); narrator.stop(); },
    actions: [{ label: 'Close', kind: 'primary', onClick: () => true }],
  });

  // Under reduced motion the whole thing still runs, but nothing slides: the
  // stylesheet drops the transitions, so it becomes a slideshow with the same
  // captions rather than something that has to be sat through.
  // Remembered from last time, but still only started from this tap.
  if (prefs().narrate) { narrator.enable(true); setVoiceLabel(); }
  resize.observe(stageEl);
  runBeat(0);
  schedule();
}
